import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { removeInfoFeature } from '../redux/actions/infoFeature';
import { v4 as uuidv4 } from 'uuid';
import {
  Card,
  CardHeader,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  Paper,
  Grid,
  Typography,
  Box,
  Chip,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Draggable from 'react-draggable';

import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';

import { innerColor, themeColor } from '../constants/themes';

const InfoPopup = () => {
  ChartJS.register(ArcElement, Tooltip, Legend);

  const dispatch = useDispatch();
  const infoFeature = useSelector((state) => state.infoFeature);

  const [areaUnit, setAreaUnit] = useState('Dekar');

  const convertAreaUnit = (value) => {
    if (areaUnit === 'Dekar') {
      return value / 1000;
    } else if (areaUnit === 'Hektar') {
      return value / 10000;
    } else if (areaUnit === 'Kilometrekare') {
      return value / 1000000;
    } else if (areaUnit === 'Metrekare') {
      return value;
    }
  };

  const unitOptions = [
    { value: 'Dekar', label: 'Dekar' },
    { value: 'Hektar', label: 'Hektar' },
    { value: 'Kilometrekare', label: 'Km²' },
    { value: 'Metrekare', label: 'm²' },
  ];

  const handleUnitChange = (event) => {
    setAreaUnit(event.target.value);
  };

  if (!infoFeature || infoFeature.length === 0) {
    return null;
  }

  const excludeProperties = ['geometry', 'urunler', 'eylulYesil'];
  const properties = infoFeature[0].properties;

  const formatPropertyName = (name) => {
    const propertyNames = {
      id: 'Parsel ID',
      il: 'İl',
      ilce: 'İlçe',
      koy: 'Köy',
      ada: 'Ada',
      parsel: 'Parsel',
      paralan: 'Parsel Alanı',
      urunalan: 'Toplam Ürün Alanı',
      urun: 'Ürün',
      alan: 'Alan',
    };
    return propertyNames[name] || name;
  };

  // Alan bilgisi taşıyan alanlar (paralan, urunalan...) artık ayrı, öne
  // çıkan "Alan Özeti" kartlarında gösteriliyor; kimlik bilgileri
  // (il/ilçe/köy/ada/parsel/id) ise kompakt bir "bilgi kutucuğu" grid'inde
  // ayrı olarak listeleniyor.
  const areaKeys = Object.keys(properties).filter(
    (key) => key === 'paralan' || key.includes('urunalan')
  );

  const identityInfo = Object.keys(properties).filter(
    (key) => !excludeProperties.includes(key) && !areaKeys.includes(key)
  );

  const colorMap = {
    hububat: '#b3e850',
    pamuk: '#5dade2',
    pamuk1: '#36A2EB',
    pamuk2: '#36A2EB',
    misir: '#f1c40f',
    mısır: '#f1c40f',
    misir1: '#FFA2A2',
    misir2: '#FFA2A2',
    tanımsız: '#9340dc',
    tanimsiz: '#9340dc',
    kontrol: '#4BC0C0',
    bosAlan: '#9E9E9E',
  };

  const productLabel = (name) => {
    const text = (name ?? '').toString().trim();
    return text || 'tanımsız';
  };

  const productColor = (name) =>
    colorMap[productLabel(name).toLowerCase()] || '#E0E0E0';

  const chipTextColor = (hex) => {
    const h = (hex || '').replace('#', '');
    if (h.length !== 6) return '#FFFFFF';
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.72 ? '#1A1A1A' : '#FFFFFF';
  };

  const fallbackColors = [
    '#FF6384',
    '#36A2EB',
    '#FFCE56',
    '#4BC0C0',
    '#9966FF',
    '#FF9F40',
  ];

  const shouldShowProductChart = properties.urunler && properties.urunler.length > 0;
  const eylulYesil = properties.eylulYesil || [];
  const eylulToplamAlan = eylulYesil.reduce((sum, item) => {
    const numericAlan = parseFloat(item.alan);
    return sum + (Number.isFinite(numericAlan) ? numericAlan : 0);
  }, 0);

  // Bir parselde birden fazla ürün çeşidi olabileceğinden (ör. hububat +
  // pamuk aynı parselde), parsel alanı / ürün alanları / boş alan kırılımı
  // tek bir yerde hesaplanıp hem öne çıkan özet kartlarında hem de grafikte
  // aynı veriden besleniyor.
  const getProductBreakdown = () => {
    const combinedUrunler = properties.urunler || [];
    const totalParselAlan = parseFloat(properties['paralan']) || 0;
    const urunlerMap = new Map();
    let totalUrunAlan = 0;

    combinedUrunler.forEach((urun) => {
      const numericAlan = parseFloat(urun.alan);
      if (Number.isFinite(numericAlan)) {
        totalUrunAlan += numericAlan;
        const label = productLabel(urun.urun);
        urunlerMap.set(label, (urunlerMap.get(label) || 0) + numericAlan);
      }
    });

    let colorIndex = 0;
    const items = [];
    urunlerMap.forEach((alan, urunAdi) => {
      const productKey = productLabel(urunAdi).toLowerCase();
      const color = colorMap[productKey] || fallbackColors[colorIndex % fallbackColors.length];
      items.push({
        urun: urunAdi,
        alan,
        percent: totalParselAlan > 0 ? (alan / totalParselAlan) * 100 : 0,
        color,
      });
      colorIndex += 1;
    });

    const bosAlanRaw = totalParselAlan - totalUrunAlan;
    const bosAlan = bosAlanRaw > 0 ? bosAlanRaw : 0;

    return {
      totalParselAlan,
      totalUrunAlan,
      bosAlan,
      bosAlanPercent: totalParselAlan > 0 ? (bosAlan / totalParselAlan) * 100 : 0,
      items,
    };
  };

  const getChartData = () => {
    const breakdown = getProductBreakdown();
    const data = {
      labels: [],
      datasets: [
        {
          data: [],
          backgroundColor: [],
        },
      ],
    };

    breakdown.items.forEach((item) => {
      data.labels.push(`${item.urun} (${item.percent.toFixed(1)}%)`);
      data.datasets[0].data.push(item.alan);
      data.datasets[0].backgroundColor.push(item.color);
    });

    if (breakdown.bosAlan > 0) {
      data.labels.push(`Boş Alan (${breakdown.bosAlanPercent.toFixed(1)}%)`);
      data.datasets[0].data.push(breakdown.bosAlan);
      data.datasets[0].backgroundColor.push(colorMap['bosAlan']);
    }

    return data;
  };

  const renderProductTable = (urunlerKey, title) => {
    const urunler = properties[urunlerKey] || [];
    if (urunler.length === 0) return null;

    return (
      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 700, display: 'block' }}>
          {title}
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 170, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', py: 0.5, fontSize: '0.7rem' }}>#</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 0.5, fontSize: '0.7rem' }}>Ürün</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 0.5, fontSize: '0.7rem' }}>Alan</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {urunler.map((urun, index) => {
                const label = productLabel(urun.urun);
                const backgroundColor = productColor(urun.urun);
                return (
                <TableRow key={uuidv4()} hover>
                  <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>{index + 1}</TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <Chip
                      label={label}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        backgroundColor,
                        color: chipTextColor(backgroundColor),
                        fontWeight: 'bold'
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>
                    <strong>
                      {urun.alan ? convertAreaUnit(urun.alan).toFixed(2) : '0'} {areaUnit}
                    </strong>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 10,
          padding: 6,
          font: {
            size: 9,
          },
        },
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = convertAreaUnit(context.parsed).toFixed(2);
            return `${label}: ${value} ${areaUnit}`;
          }
        }
      }
    },
  };

  const breakdown = getProductBreakdown();

  return (
    <div id="popup" className="ol-popup">
      <Draggable>
        <Card
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95vw', sm: '80vw', md: '65vw', lg: '48vw' },
            maxWidth: '720px',
            maxHeight: '80vh',
            zIndex: 99999,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            // Modern bir görünüm için hafif saydamlık + arka plan bulanıklığı
            backgroundColor: 'rgba(255, 255, 255, 0.94)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
          }}
        >
          <CardHeader
            sx={{
              color: innerColor,
              backgroundColor: alpha(themeColor, 0.94),
              flexShrink: 0,
              py: 1,
              px: 2,
            }}
            title="Parsel Öznitelikleri"
            titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700 }}
            action={
              <IconButton
                size="small"
                sx={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                onClick={() => dispatch(removeInfoFeature())}
              >
                <CloseIcon sx={{ color: 'white', fontSize: '1.1rem' }} />
              </IconButton>
            }
          />

          <CardContent
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              p: 1.5,
              '&:last-child': { pb: 1.5 },
            }}
          >
            {/* Üst şerit: başlık + Alan Birimi Seçici */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" fontWeight={700} sx={{ color: 'text.secondary' }}>
                Alan Özeti
              </Typography>
              <FormControl size="small" sx={{ minWidth: 110 }}>
                <Select
                  value={areaUnit}
                  onChange={handleUnitChange}
                  sx={{ fontSize: '0.75rem', '& .MuiSelect-select': { py: 0.5 } }}
                >
                  {unitOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.75rem' }}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Öne çıkan Alan Özeti kartları: kullanıcı popup'ı açar açmaz
                ilk gördüğü bilgi burası olsun diye en üstte. Bir parselde
                birden fazla ürün çeşidi olabileceği için her ürün kendi
                kartında, dinamik olarak listeleniyor. */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                gap: 1,
                mb: 1.5,
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 1.25,
                  borderRadius: 2,
                  backgroundColor: alpha('#1976D2', 0.08),
                  border: '1px solid',
                  borderColor: alpha('#1976D2', 0.35),
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ display: 'block', color: '#1976D2', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.62rem', letterSpacing: 0.3 }}
                >
                  Parsel Alanı
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1976D2', lineHeight: 1.25 }}>
                  {convertAreaUnit(breakdown.totalParselAlan).toFixed(2)}
                  <Typography component="span" variant="caption" sx={{ ml: 0.5, fontWeight: 600 }}>
                    {areaUnit}
                  </Typography>
                </Typography>
              </Paper>

              {breakdown.items.map((item) => (
                <Paper
                  key={item.urun}
                  elevation={0}
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    backgroundColor: alpha(item.color, 0.14),
                    border: '1px solid',
                    borderColor: alpha(item.color, 0.4),
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', color: item.color, fontWeight: 700, textTransform: 'uppercase', fontSize: '0.62rem', letterSpacing: 0.3 }}
                  >
                    {item.urun}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.25 }}>
                    {convertAreaUnit(item.alan).toFixed(2)}
                    <Typography component="span" variant="caption" sx={{ ml: 0.5, fontWeight: 600 }}>
                      {areaUnit}
                    </Typography>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    %{item.percent.toFixed(1)} pay
                  </Typography>
                </Paper>
              ))}

              {eylulYesil.length > 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    backgroundColor: alpha('#2E7D32', 0.14),
                    border: '1px solid',
                    borderColor: alpha('#2E7D32', 0.4),
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', color: '#2E7D32', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.62rem', letterSpacing: 0.3 }}
                  >
                    Eylül Yeşil
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.25 }}>
                    {convertAreaUnit(eylulToplamAlan).toFixed(2)}
                    <Typography component="span" variant="caption" sx={{ ml: 0.5, fontWeight: 600 }}>
                      {areaUnit}
                    </Typography>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {eylulYesil.length} poligon
                  </Typography>
                </Paper>
              )}

              {breakdown.bosAlan > 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    backgroundColor: 'grey.100',
                    border: '1px solid',
                    borderColor: 'grey.300',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.62rem', letterSpacing: 0.3 }}
                  >
                    Boş Alan
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.secondary', lineHeight: 1.25 }}>
                    {convertAreaUnit(breakdown.bosAlan).toFixed(2)}
                    <Typography component="span" variant="caption" sx={{ ml: 0.5, fontWeight: 600 }}>
                      {areaUnit}
                    </Typography>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    %{breakdown.bosAlanPercent.toFixed(1)} pay
                  </Typography>
                </Paper>
              )}
            </Box>

            {/* Kompakt kimlik bilgileri: İl, İlçe, Köy, Ada, Parsel, ID */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(96px, 1fr))',
                gap: 0.75,
                mb: 1.5,
              }}
            >
              {identityInfo.map((header) => (
                <Box
                  key={header}
                  sx={{
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    backgroundColor: 'grey.50',
                    border: '1px solid',
                    borderColor: 'grey.200',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', color: 'text.secondary', fontSize: '0.65rem', lineHeight: 1.2 }}
                  >
                    {formatPropertyName(header)}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem', lineHeight: 1.3 }}>
                    {properties[header]}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Ürün Detayları: her zaman açık, tablo ve grafik birlikte. */}
            <Box>
              <Typography variant="body2" fontWeight={700} sx={{ mb: 0.75 }}>
                Ürün Detayları
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={6}>
                  {shouldShowProductChart ? (
                    renderProductTable('urunler', 'Ürünler')
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: 80,
                        mb: 1,
                        backgroundColor: '#f5f5f5',
                        borderRadius: 1,
                        border: '1px dashed #ccc',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Bu parsel için ürün verisi bulunmuyor
                      </Typography>
                    </Box>
                  )}
                  {eylulYesil.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 700, display: 'block' }}>
                        Eylül Yeşil
                      </Typography>
                      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 170, overflow: 'auto' }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold', py: 0.5, fontSize: '0.7rem' }}>#</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', py: 0.5, fontSize: '0.7rem' }}>Katman</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', py: 0.5, fontSize: '0.7rem' }}>Alan</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {eylulYesil.map((item, index) => (
                              <TableRow key={item.id ?? index} hover>
                                <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>{index + 1}</TableCell>
                                <TableCell sx={{ py: 0.5 }}>
                                  <Chip
                                    label="Eylül Yeşil"
                                    size="small"
                                    sx={{
                                      height: 20,
                                      fontSize: '0.65rem',
                                      backgroundColor: '#2E7D32',
                                      color: '#FFFFFF',
                                      fontWeight: 'bold',
                                    }}
                                  />
                                </TableCell>
                                <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>
                                  <strong>
                                    {item.alan ? convertAreaUnit(item.alan).toFixed(2) : '0'} {areaUnit}
                                  </strong>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
                </Grid>
                {shouldShowProductChart && (
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 1, height: 180 }}>
                      <Doughnut data={getChartData()} options={chartOptions} />
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>
          </CardContent>
        </Card>
      </Draggable>
    </div>
  );
};

export default InfoPopup;
